/*
 * Canadian Off Grid — sales helpers on product, kit collection and cart pages.
 * Rendered by snippets/cog-sales-assist.liquid, which prints the page's facts
 * as JSON (#cog-sales-assist-config). Three pieces, each only where it applies:
 *
 * - PayPlan by RBC: "as low as $X/month" under a product's price and the cart
 *   subtotal, or "Pay over time with PayPlan by RBC" when the representative
 *   rate and term aren't set. Same maths as the shop's own PayPlan rule
 *   (amortized, rounded up to the cent; nothing outside the purchase range).
 * - A delivered-price estimate on freight products: postal code in, the
 *   cheapest truck freight estimate out, so freight isn't a surprise at the
 *   cart. Checkout confirms the final rate.
 * - "Talk to an expert" on big-ticket products and the kit collections: book
 *   a consultation, or ask for a quote through the store's app proxy
 *   (/apps/book/quote). A person answers; nothing is priced automatically.
 */
(function () {
  "use strict";

  var node = document.getElementById("cog-sales-assist-config");
  if (!node) return;
  var config;
  try { config = JSON.parse(node.textContent || "{}"); } catch (_) { return; }

  function money(cents) {
    try {
      return new Intl.NumberFormat("en-CA", { style: "currency", currency: "CAD" }).format(cents / 100);
    } catch (_) {
      return "$" + (cents / 100).toFixed(2);
    }
  }

  // ---- PayPlan ---------------------------------------------------------

  /** The level monthly payment (cents, rounded up) that repays a price over a term at an APR. */
  function payPlanMonthlyCents(priceCents, aprPct, months) {
    if (priceCents <= 0 || months <= 0) return 0;
    var r = aprPct / 100 / 12;
    var payment = r === 0 ? priceCents / months : (priceCents * r) / (1 - Math.pow(1 + r, -months));
    return Math.ceil(payment - 1e-9);
  }

  /** The line to show for a price, or null when PayPlan doesn't apply. */
  function payPlanText(priceCents, plan) {
    if (!plan || !(priceCents >= plan.minCents) || priceCents > plan.maxCents) return null;
    if (plan.aprPct != null && plan.termMonths != null) {
      return "As low as " + money(payPlanMonthlyCents(priceCents, plan.aprPct, plan.termMonths)) + "/month with PayPlan by RBC";
    }
    return "Pay over time with PayPlan by RBC";
  }

  function payPlanLine() {
    var line = document.createElement("p");
    line.className = "cog-sa-payplan";
    line.hidden = true;
    var text = document.createElement("span");
    var link = document.createElement("a");
    link.href = (config.payplan && config.payplan.learnMorePath) || "/pages/financing";
    link.textContent = "Learn more";
    line.appendChild(text);
    line.appendChild(document.createTextNode(" · "));
    line.appendChild(link);
    return {
      el: line,
      set: function (priceCents) {
        var t = payPlanText(priceCents, config.payplan);
        line.hidden = !t;
        if (t && text.textContent !== t) text.textContent = t;
      },
    };
  }

  // ---- Product page ----------------------------------------------------

  var product = config.product || null;

  function productForm() {
    return document.querySelector(".product-info form[action*='/cart/add']") || document.querySelector("form[action*='/cart/add']");
  }

  function selectedVariant() {
    if (!product) return null;
    var form = productForm();
    var input = form && form.querySelector("[name='id']");
    var id = Number((input && input.value) || product.selectedVariantId);
    for (var i = 0; i < product.variants.length; i++) if (Number(product.variants[i].id) === id) return product.variants[i];
    return product.variants[0] || null;
  }

  function selectedQuantity() {
    var form = productForm();
    var input = (form && form.querySelector("[name='quantity']")) || document.querySelector(".product-info [name='quantity']");
    var n = Number(input && input.value);
    return Number.isInteger(n) && n > 0 && n < 1000 ? n : 1;
  }

  function el(tag, cls, text) {
    var e = document.createElement(tag);
    if (cls) e.className = cls;
    if (text != null) e.textContent = text;
    return e;
  }

  function formatPostal(value) {
    var c = String(value || "").toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 6);
    return c.length > 3 ? c.slice(0, 3) + " " + c.slice(3) : c;
  }
  function validPostal(value) { return /^[A-Z][0-9][A-Z] [0-9][A-Z][0-9]$/.test(formatPostal(value)); }
  function rememberedPostal() { try { return formatPostal(window.localStorage.getItem("gru-shipping-postal") || ""); } catch (_) { return ""; } }
  function rememberPostal(p) { try { window.localStorage.setItem("gru-shipping-postal", p); } catch (_) {} }

  /** The freight estimate box. The postal code is shared with the cart's freight calculator. */
  function freightEstimate() {
    var apiBase = window.GruLtlPrequoteConfig && window.GruLtlPrequoteConfig.apiBase;
    if (!apiBase) return null;
    var box = el("div", "cog-sa-freight");
    box.appendChild(el("p", "cog-sa-freight__title", "Estimate delivery to your door"));
    var row = el("div", "cog-sa-freight__row");
    var input = el("input", "input cog-sa-freight__postal");
    input.type = "text";
    input.maxLength = 7;
    input.placeholder = "Postal code";
    input.setAttribute("autocomplete", "postal-code");
    input.setAttribute("aria-label", "Postal code");
    input.value = rememberedPostal();
    var button = el("button", "btn btn--secondary cog-sa-freight__go", "Estimate");
    button.type = "button";
    row.appendChild(input);
    row.appendChild(button);
    box.appendChild(row);
    var status = el("p", "cog-sa-freight__status");
    status.setAttribute("role", "status");
    status.setAttribute("aria-live", "polite");
    box.appendChild(status);
    var run = 0;

    function estimate() {
      var postal = formatPostal(input.value);
      input.value = postal;
      if (!validPostal(postal)) { status.textContent = "Enter a Canadian postal code, like A1A 1A1."; return; }
      rememberPostal(postal);
      var variant = selectedVariant();
      if (!variant) return;
      var mine = ++run;
      button.disabled = true;
      status.textContent = "Estimating freight to " + postal + "…";
      var controller = typeof AbortController === "function" ? new AbortController() : null;
      var timer = window.setTimeout(function () { if (controller) controller.abort(); }, 45000);
      fetch(apiBase + "/api/cog/dayross/product-quote", {
        method: "POST",
        mode: "cors",
        headers: { "Content-Type": "application/json" },
        signal: controller ? controller.signal : undefined,
        body: JSON.stringify({
          requestSurface: "product_page",
          postal: postal,
          items: [{
            variantId: String(variant.id),
            sku: variant.sku || null,
            title: product.title,
            quantity: selectedQuantity(),
            price: Number(variant.price) / 100,
            grams: Number(variant.grams) || 0,
          }],
        }),
      })
        .then(function (r) { return r.json().catch(function () { return {}; }); })
        .then(function (body) {
          if (mine !== run) return;
          var cents = cheapestFreightCents(body);
          status.textContent = cents == null
            ? "We couldn't estimate freight to " + postal + " just now. Your freight cost is calculated in the cart."
            : "Estimated delivery to " + postal + ": " + money(cents) + " by truck. Checkout confirms the final rate.";
        })
        .catch(function () {
          if (mine === run) status.textContent = "We couldn't estimate freight just now. Your freight cost is calculated in the cart.";
        })
        .then(function () {
          window.clearTimeout(timer);
          if (mine === run) button.disabled = false;
        });
    }

    button.addEventListener("click", estimate);
    input.addEventListener("input", function () { input.value = formatPostal(input.value); });
    input.addEventListener("keydown", function (e) { if (e.key === "Enter") { e.preventDefault(); estimate(); } });
    return { el: box, reset: function () { run++; status.textContent = ""; button.disabled = false; } };
  }

  /** The cheapest priced option, in cents, from the product-quote answer. */
  function cheapestFreightCents(body) {
    var quotes = (body && Array.isArray(body.quotes)) ? body.quotes : [];
    var best = null;
    for (var i = 0; i < quotes.length; i++) {
      var amount = Number(quotes[i] && quotes[i].amount);
      if (!Number.isFinite(amount) || amount <= 0) continue;
      var cents = Math.round(amount * 100);
      if (best == null || cents < best) best = cents;
    }
    return best;
  }

  // ---- Talk to an expert -----------------------------------------------

  function expertPanel(subject) {
    var panel = el("section", "cog-sa-expert");
    panel.setAttribute("aria-label", "Talk to an expert");
    panel.appendChild(el("h2", "cog-sa-expert__title h5", "Talk to an expert"));
    panel.appendChild(el("p", "cog-sa-expert__lead", "Not sure this is the right fit? Our team sizes systems like this every day. Book a call, or tell us about your project and we'll send you a quote."));
    var actions = el("div", "cog-sa-expert__actions");
    var book = el("a", "btn btn--primary", "Book a consultation");
    book.href = config.consultationUrl || "/products/visit-our-showroom-book-your-consultation";
    var ask = el("button", "btn btn--secondary", "Get a quote for this kit");
    ask.type = "button";
    ask.setAttribute("aria-expanded", "false");
    actions.appendChild(book);
    actions.appendChild(ask);
    panel.appendChild(actions);

    var form = el("form", "cog-sa-quote");
    form.hidden = true;
    form.noValidate = true;
    function field(name, label, type, attrs) {
      var wrap = el("label", "cog-sa-quote__field");
      wrap.appendChild(el("span", null, label));
      var input = el(type === "textarea" ? "textarea" : "input", type === "textarea" ? "input" : "input");
      if (type !== "textarea") input.type = type;
      input.name = name;
      for (var k in attrs || {}) input.setAttribute(k, attrs[k]);
      wrap.appendChild(input);
      form.appendChild(wrap);
      return input;
    }
    var nameInput = field("name", "Name", "text", { autocomplete: "name", required: "required", maxlength: "120" });
    field("email", "Email", "email", { autocomplete: "email", maxlength: "200" });
    field("phone", "Phone", "tel", { autocomplete: "tel", maxlength: "40" });
    var postal = field("postal", "Postal code (where it ships)", "text", { autocomplete: "postal-code", maxlength: "7" });
    postal.value = rememberedPostal();
    field("note", "What are you powering? (optional)", "textarea", { rows: "3", maxlength: "1000" });
    // Left empty by people; filled by bots.
    var trap = el("input");
    trap.type = "text";
    trap.name = "website";
    trap.tabIndex = -1;
    trap.autocomplete = "off";
    trap.className = "cog-sa-quote__trap";
    trap.setAttribute("aria-hidden", "true");
    form.appendChild(trap);
    var send = el("button", "btn btn--primary", "Send my request");
    send.type = "submit";
    form.appendChild(send);
    var message = el("p", "cog-sa-quote__message");
    message.setAttribute("role", "status");
    message.setAttribute("aria-live", "polite");
    form.appendChild(message);
    panel.appendChild(form);

    ask.addEventListener("click", function () {
      form.hidden = !form.hidden;
      ask.setAttribute("aria-expanded", String(!form.hidden));
      if (!form.hidden) nameInput.focus();
    });

    form.addEventListener("submit", function (event) {
      event.preventDefault();
      var data = {};
      Array.prototype.forEach.call(form.elements, function (f) { if (f.name) data[f.name] = f.value; });
      if (!String(data.name || "").trim()) { message.textContent = "Add your name."; return; }
      if (!String(data.email || "").trim() && !String(data.phone || "").trim()) {
        message.textContent = "Add an email or a phone number so we can send your quote.";
        return;
      }
      var s = subject();
      data.productTitle = s.title;
      data.productHandle = s.handle || "";
      data.variantId = s.variantId ? String(s.variantId) : "";
      data.quantity = s.quantity || 1;
      if (s.priceCents) data.priceCents = s.priceCents;
      send.disabled = true;
      message.textContent = "Sending…";
      fetch(config.quoteUrl || "/apps/book/quote", {
        method: "POST",
        credentials: "same-origin",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify(data),
      })
        .then(function (r) { return r.json().catch(function () { return {}; }).then(function (b) { return { ok: r.ok, body: b }; }); })
        .then(function (res) {
          if (res.ok && res.body && res.body.ok) {
            form.reset();
            form.hidden = true;
            ask.hidden = true;
            var done = el("p", "cog-sa-expert__done", "Thanks — we have your request. One of our team will be in touch soon with your quote.");
            panel.appendChild(done);
            try { window.dataLayer && window.dataLayer.push({ event: "generate_lead", lead_type: "kit_quote", item_name: s.title }); } catch (_) {}
            return;
          }
          message.textContent = (res.body && res.body.error) || "We couldn't send that just now. Call us or try again.";
        })
        .catch(function () { message.textContent = "We couldn't send that just now. Call us or try again."; })
        .then(function () { send.disabled = false; });
    });
    return panel;
  }

  // ---- Mounting ----------------------------------------------------------

  function mountProduct() {
    var priceBlock = document.querySelector(".product-info__price");
    var pay = payPlanLine();
    if (priceBlock && priceBlock.parentNode) priceBlock.parentNode.insertBefore(pay.el, priceBlock.nextSibling);

    var anchor = document.querySelector(".product-info product-form") || productForm();
    var after = anchor && anchor.parentNode ? anchor : null;
    var estimate = product.freight ? freightEstimate() : null;
    var expert = null;

    function bigTicket(v) { return config.kit || (v && Number(v.price) * selectedQuantity() >= Number(config.expertMinCents || 300000)); }
    function subject() {
      var v = selectedVariant();
      return { title: product.title, handle: product.handle, variantId: v && v.id, quantity: selectedQuantity(), priceCents: v ? Number(v.price) : null };
    }

    function insertAfter(child) {
      if (!after) return;
      after.parentNode.insertBefore(child, after.nextSibling);
      after = child;
    }
    if (estimate) insertAfter(estimate.el);

    var lastKey = "";
    function refresh() {
      var v = selectedVariant();
      var key = (v && v.id) + "|" + selectedQuantity();
      if (key === lastKey) return;
      var variantChanged = lastKey.split("|")[0] !== String(v && v.id);
      lastKey = key;
      pay.set(v ? Number(v.price) : 0);
      if (estimate && variantChanged) estimate.reset();
      if (!expert && bigTicket(v)) {
        expert = expertPanel(subject);
        insertAfter(expert);
      }
    }
    refresh();
    document.addEventListener("change", refresh);
    document.addEventListener("input", refresh);
    window.setInterval(refresh, 1000);
  }

  function mountCollection() {
    var panel = expertPanel(function () {
      return { title: (config.collection && config.collection.title) || "a kit", handle: "", variantId: null, quantity: 1, priceCents: null };
    });
    panel.classList.add("cog-sa-expert--collection");
    // Below the product grid, outside the part filtering re-renders.
    var grid = document.querySelector("[data-num-results]");
    if (grid && grid.parentNode) grid.parentNode.insertBefore(panel, grid.nextSibling);
    else (document.querySelector("main") || document.body).appendChild(panel);
  }

  function mountCart() {
    var heading = Array.prototype.find.call(document.querySelectorAll("main h3, main h2"), function (h) { return /subtotal/i.test(h.textContent || ""); });
    var row = heading && heading.parentNode;
    if (!row || !row.parentNode) return;
    var pay = payPlanLine();
    pay.el.classList.add("cog-sa-payplan--cart");
    row.parentNode.insertBefore(pay.el, row.nextSibling);
    pay.set(Number(config.cartTotalCents) || 0);
    function reread() {
      fetch(((window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || "/") + "cart.js", { credentials: "same-origin", headers: { Accept: "application/json" } })
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (cart) { if (cart) pay.set(Number(cart.total_price) || 0); })
        .catch(function () {});
    }
    ["cart:updated", "cart:refresh", "cart:change"].forEach(function (n) { document.addEventListener(n, reread); });
  }

  function start() {
    if (config.page === "product" && product) mountProduct();
    else if (config.page === "collection" && config.kit) mountCollection();
    else if (config.page === "cart") mountCart();
  }

  if (config.testMode) {
    window.__CogSalesAssistTest = { payPlanMonthlyCents: payPlanMonthlyCents, payPlanText: payPlanText, cheapestFreightCents: cheapestFreightCents, formatPostal: formatPostal };
    return;
  }
  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", start);
  else start();
})();
